import React, { useState, useEffect, useRef } from 'react';
import api from '../api';
import { useNavigate } from 'react-router-dom';
import '../styles/Messages.css';

function Messages({ user, showMessages, setShowMessages }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const messagesRef = useRef(null);

  useEffect(() => {
    if (showMessages) {
      loadMessages();
    }
  }, [showMessages]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (messagesRef.current && !messagesRef.current.contains(event.target)) {
        setShowMessages(false);
      }
    };

    if (showMessages) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showMessages]);

  const loadMessages = async () => {
    setLoading(true);
    try {
      const response = await api.getAllMessages();
      setMessages(response.data);
    } catch (error) {
      console.error('Error loading messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMessageClick = (message) => {
    // Navigate to tasks page with the task opened
    navigate('/tasks', {
      state: {
        openTaskId: message.task_id,
        openMeetingId: message.meeting_id._id || message.meeting_id,
        scrollToMessages: true
      }
    });
    setShowMessages(false);
  };

  const formatTimestamp = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) {
      const diffInMinutes = Math.floor((now - date) / (1000 * 60));
      return diffInMinutes < 1 ? 'Just now' : `${diffInMinutes}m ago`;
    } else if (diffInHours < 24) {
      return `${diffInHours}h ago`;
    } else {
      const diffInDays = Math.floor(diffInHours / 24);
      return diffInDays === 1 ? '1 day ago' : `${diffInDays} days ago`;
    }
  };

  if (!showMessages) return null;

  return (
    <div className="messages-dropdown" ref={messagesRef}>
          <div className="messages-header">
            <h3>Task Discussions</h3>
            <button 
              className="close-messages"
              onClick={() => setShowMessages(false)}
            >
              ✕
            </button>
          </div>

          <div className="messages-list">
            {loading ? (
              <div className="messages-loading">
                <p>Loading messages...</p>
              </div>
            ) : messages.length === 0 ? (
              <div className="no-messages">
                <p>No messages yet</p>
                <span>Start a discussion in any task</span>
              </div>
            ) : (
              messages.map((message, index) => (
                <div
                  key={index}
                  className="message-item"
                  onClick={() => handleMessageClick(message)}
                >
                  <div className="message-avatar">
                    {message.sender?.full_name ? 
                      message.sender.full_name.charAt(0).toUpperCase() :
                      message.sender?.username?.charAt(0).toUpperCase() || '?'
                    }
                  </div>
                  <div className="message-content">
                    <div className="message-header-row">
                      <span className="message-sender">
                        {message.sender?.full_name || message.sender?.username || 'Unknown'}
                      </span>
                      <span className="message-time">
                        {formatTimestamp(message.created_at)}
                      </span>
                    </div>
                    <div className="message-task-info">
                      {message.meeting_title} • {message.task_description}
                    </div>
                    <p className="message-text">{message.message}</p>
                  </div>
                </div>
              ))
            )}
          </div>

          {messages.length > 0 && (
            <div className="messages-footer">
              <button 
                className="view-all-btn"
                onClick={() => {
                  navigate('/tasks');
                  setShowMessages(false);
                }}
              >
                View All in Tasks
              </button>
            </div>
          )}
        </div>
  );
}

export default Messages;
